import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase/client';
import InvestorCalendar from './InvestorCalendar';
import InvestorMetricModal, { InvestorMetricKey } from './InvestorMetricModal';
import './InvestorOverview.css';

interface InvestorStats {
  total_patients: number;
  consults_month: number;
  revenue_month: number;
  revenue_total: number;
  upcoming: number;
  next_consult: string | null;
}

interface InvestorOverviewProps {
  investor: {
    id: string;
    rut: string;
    nombre: string;
    apellido: string;
    email: string;
    telefono?: string;
    tipo_inversionista: string;
    porcentaje_participacion: number;
    fecha_ingreso: string;
  };
}

const TIPO_LABEL: Record<string, string> = {
  socio:         'Socio',
  inversionista: 'Inversionista',
  fundador:      'Fundador',
};

const formatCLP = (amount: number) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount);

const formatCompact = (amount: number): { primary: string; secondary: string } => {
  const full = formatCLP(amount);
  if (amount >= 1_000_000_000) {
    return {
      primary: `$${new Intl.NumberFormat('es-CL', { maximumFractionDigits: 1 }).format(amount / 1_000_000_000)}B`,
      secondary: full,
    };
  }
  if (amount >= 1_000_000) {
    return {
      primary: `$${new Intl.NumberFormat('es-CL', { maximumFractionDigits: 1 }).format(amount / 1_000_000)}M`,
      secondary: full,
    };
  }
  return { primary: full, secondary: '' };
};

const buildKPIs = (stats: InvestorStats): Array<{
  key: InvestorMetricKey;
  label: string;
  primary: string;
  secondary: string;
  icon: string;
  color: string;
  bg: string;
}> => [
  {
    key: 'patients',
    label: 'Pacientes en cartera',
    primary: stats.total_patients.toString(),
    secondary: stats.total_patients === 1 ? 'paciente activo' : 'pacientes activos',
    icon: 'fas fa-users',
    color: 'var(--apple-blue)',
    bg: 'rgba(0, 122, 255, 0.10)',
  },
  {
    key: 'consultsMonth',
    label: 'Consultas este mes',
    primary: stats.consults_month.toString(),
    secondary: stats.upcoming > 0 ? `${stats.upcoming} por venir` : 'al día',
    icon: 'fas fa-calendar-day',
    color: '#C05E00',
    bg: 'rgba(192, 94, 0, 0.10)',
  },
  {
    key: 'revenueMonth',
    label: 'Ingresos del mes',
    ...formatCompact(stats.revenue_month),
    icon: 'fas fa-chart-line',
    color: 'var(--success-text, #057A55)',
    bg: 'rgba(52, 199, 89, 0.10)',
  },
  {
    key: 'revenueTotal',
    label: 'Ingresos totales',
    ...formatCompact(stats.revenue_total),
    icon: 'fas fa-piggy-bank',
    color: '#6B21A8',
    bg: 'rgba(107, 33, 168, 0.10)',
  },
];

const InvestorOverview: React.FC<InvestorOverviewProps> = ({ investor }) => {
  const [profileExpanded, setProfileExpanded] = useState(false);
  const [stats, setStats] = useState<InvestorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [openMetric, setOpenMetric] = useState<InvestorMetricKey | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_investor_stats', {
        p_inversionista_id: investor.id,
      });
      if (error) {
        console.error('Error cargando stats inversor:', error.code);
      } else {
        const row = Array.isArray(data) ? data[0] : data;
        setStats(row as InvestorStats);
      }
      setLoading(false);
    };
    load();
  }, [investor.id]);

  const initials = `${investor.nombre[0] ?? ''}${investor.apellido[0] ?? ''}`.toUpperCase();
  const tipoLabel = TIPO_LABEL[investor.tipo_inversionista] ?? investor.tipo_inversionista;

  const kpis = stats ? buildKPIs(stats) : [];

  return (
    <div className="dashboard-home">

      {/* ── Tarjeta de perfil ── */}
      <div className={`therapist-summary-card${profileExpanded ? ' expanded' : ''}`}>
        <div className="summary-card__bar">
          <div className="summary-card__identity">
            <div className="summary-avatar">
              <div className="summary-avatar__initials">
                {initials}
              </div>
            </div>
            <div className="summary-card__meta">
              <h2 className="summary-card__name">
                {investor.nombre} {investor.apellido}
              </h2>
              <p className="summary-card__role">
                {tipoLabel}
                <span className="io-participation-badge">
                  {investor.porcentaje_participacion}% participación
                </span>
              </p>
            </div>
          </div>
          <button
            className="summary-card__toggle"
            onClick={() => setProfileExpanded(v => !v)}
            aria-expanded={profileExpanded}
            title={profileExpanded ? 'Cerrar' : 'Ver información personal'}
          >
            <i className={`fas fa-chevron-${profileExpanded ? 'up' : 'down'}`} />
          </button>
        </div>

        {profileExpanded && (
          <div className="summary-card__detail">
            <div className="summary-info-grid">
              <div className="summary-info-row">
                <span className="summary-info-label">RUT</span>
                <span className="summary-info-value">{investor.rut}</span>
              </div>
              <div className="summary-info-row">
                <span className="summary-info-label">Email</span>
                <span className="summary-info-value">{investor.email}</span>
              </div>
              {investor.telefono && (
                <div className="summary-info-row">
                  <span className="summary-info-label">Teléfono</span>
                  <span className="summary-info-value">{investor.telefono}</span>
                </div>
              )}
              <div className="summary-info-row">
                <span className="summary-info-label">Tipo</span>
                <span className="summary-info-value">{tipoLabel}</span>
              </div>
              <div className="summary-info-row">
                <span className="summary-info-label">Participación</span>
                <span className="summary-info-value io-info-value--accent">
                  {investor.porcentaje_participacion}%
                </span>
              </div>
              <div className="summary-info-row">
                <span className="summary-info-label">Miembro desde</span>
                <span className="summary-info-value">
                  {new Date(investor.fecha_ingreso).toLocaleDateString('es-CL', {
                    day: 'numeric', month: 'long', year: 'numeric',
                  })}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── KPI Grid ── */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner" />
            <p>Cargando datos...</p>
          </div>
        </div>
      )}

      {!loading && stats && (
        <div className="io-kpi-grid">
          {kpis.map(kpi => (
            <button
              type="button"
              key={kpi.key}
              className="io-kpi-card io-kpi-card--clickable"
              onClick={() => setOpenMetric(kpi.key)}
              aria-haspopup="dialog"
            >
              <div className="io-kpi-card__header">
                <span className="io-kpi-card__label">
                  {kpi.label}
                </span>
                {/* Fondo/color por KPI: data-driven (buildKPIs), se mantiene inline */}
                <div className="io-kpi-card__icon" style={{ background: kpi.bg }}>
                  <i className={kpi.icon} style={{ color: kpi.color }} />
                </div>
              </div>
              <div>
                <div className="io-kpi-card__value">
                  {kpi.primary}
                </div>
                {kpi.secondary && (
                  <div className="io-kpi-card__secondary">
                    {kpi.secondary}
                  </div>
                )}
              </div>
              <span className="io-kpi-card__more">
                Ver detalle <i className="fas fa-chevron-right" style={{ fontSize: 9 }} aria-hidden="true" />
              </span>
            </button>
          ))}
        </div>
      )}

      {openMetric && (
        <InvestorMetricModal
          investorId={investor.id}
          metric={openMetric}
          onClose={() => setOpenMetric(null)}
        />
      )}

      {!loading && stats && stats.upcoming === 0 && stats.total_patients === 0 && (
        <div className="io-empty-state">
          <i className="fas fa-info-circle io-empty-state__icon" />
          <p className="io-empty-state__text">
            Aún no tenés pacientes asignados a tu cartera.<br />
            El director clínico puede vincularte pacientes existentes.
          </p>
        </div>
      )}

      {/* ── Agenda ── */}
      <div className="overview-calendar-section">
        <InvestorCalendar investor={investor} />
      </div>
    </div>
  );
};

export default InvestorOverview;
